import '@fontsource-variable/inter';
import '@fontsource-variable/jetbrains-mono';
import '@fontsource-variable/noto-sans-arabic';
import './styles/index.css';

import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/lib/auth/auth';
import { isApiError, isRetryable } from '@/lib/api/errors';
import { restoreSession } from '@/lib/api/http';
import { initI18n } from '@/lib/i18n';
import { usePreferences } from '@/lib/preferences';
import { router } from './app/router';

const queryClient = new QueryClient({
  queryCache: new QueryCache(),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      // Network errors, 503 and 429 are worth another try; a 4xx answer will not change.
      retry: (count, error) => count < 2 && isRetryable(error) && !(isApiError(error) && error.status >= 400 && error.status < 500 && error.status !== 429),
    },
    mutations: { retry: false },
  },
});

/** Re-evaluates route guards when the session starts or ends. */
function Routes() {
  const { status, restoring } = useAuth();
  const { theme } = usePreferences();
  // Guards read the session at navigation time: re-run them when it starts or ends (sign-in, sign-out, expiry).
  useEffect(() => { void router.invalidate(); }, [status]);
  if (restoring) return <div className="grid min-h-dvh place-items-center" role="status" aria-label="Loading"><div className="size-8 animate-spin rounded-full border-2 border-border-strong border-t-accent" /></div>;
  return (
    <>
      <RouterProvider router={router} />
      <Toaster position="bottom-right" richColors closeButton theme={theme === 'system' ? 'system' : theme} toastOptions={{ duration: 4500 }} />
    </>
  );
}

// A page reload opens the session again from the refresh cookie before the first render (the console and support tickets have their own sessions; the customer KYC form has none).
const restoring = /^\/(platform-admin|support-login|reset-password|kyc-form)/.test(location.pathname) ? Promise.resolve(false) : restoreSession();
await Promise.all([initI18n(), restoring]);
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider><Routes /></AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);
